import { ReactNode, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface CodeBlockProps {
    code: string;
}

/**
 * Простой блок с кодом и кнопкой копирования.
 */
function CodeBlock({ code }: CodeBlockProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                pr: 5,
                overflowX: 'auto',
            }}
        >
            <Typography component="pre" variant="body2" sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre' }}>
                {code}
            </Typography>
            <Tooltip title={copied ? 'Скопировано' : 'Копировать'}>
                <IconButton size="small" onClick={handleCopy} sx={{ position: 'absolute', top: 4, right: 4 }}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

interface SectionProps {
    title: string;
    children: ReactNode;
}

function Section({ title, children }: SectionProps) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {title}
            </Typography>
            {children}
        </Box>
    );
}

interface ParamRow {
    name: string;
    type: string;
    description: string;
}

const endpoints: ParamRow[] = [
    { name: 'GET /nodes', type: '—', description: 'Список доступных узлов.' },
    { name: 'GET /locations', type: '—', description: 'Список доступных локаций.' },
    { name: 'GET /devices', type: '—', description: 'Список всех доступных устройств.' },
    { name: 'GET /sensors', type: '—', description: 'Список всех доступных датчиков.' },
    { name: 'GET /measurements/size', type: '—', description: 'Количество измерений по заданному фильтру.' },
    { name: 'GET /measurements', type: '—', description: 'Измерения по заданному фильтру.' },
];

const params: ParamRow[] = [
    { name: 'node', type: 'number', description: 'ID дочернего узла. Если не указан — используется узел токена.' },
    { name: 'dev', type: 'number[]', description: 'ID устройств через запятую, например dev=1,2,3.' },
    { name: 'sns', type: 'number[]', description: 'ID датчиков через запятую.' },
    { name: 'lo', type: 'number[]', description: 'ID локаций через запятую.' },
    {
        name: 'ov',
        type: 'boolean',
        description: 'Признак просроченной передачи: true или false. Если не указан — то все.',
    },
    { name: 'fr', type: 'date', description: 'Начало периода (ISO 8601), например 2026-09-01T00:00:00Z.' },
    { name: 'to', type: 'date', description: 'Конец периода (ISO 8601).' },
    {
        name: 'column',
        type: 'string',
        description: 'Поле сортировки: device, sensor, measure, transaction, overdue. По умолчанию measure.',
    },
    { name: 'order', type: 'string', description: 'Направление сортировки: ASC или DESC. По умолчанию ASC.' },
    {
        name: 'offset',
        type: 'number',
        description: 'Смещение выборки. Наличие offset/limit включает постраничный режим.',
    },
    { name: 'limit', type: 'number', description: 'Размер страницы (по умолчанию 500 в постраничном режиме).' },
];

const responseExample = `[
  {
    "device": 12,
    "sensor": 34,
    "measure": "2026-09-25T10:00:00.000Z",
    "transaction": "2026-09-25T10:00:05.000Z",
    "overdue": false,
    "values": [1.2, 3.4, 5.6],
    "depths": [0, 10, 20]
  }
]`;

interface TokenDocsDialogProps {
    open: boolean;
    onClose: () => void;
}

function TokenDocsDialog({ open, onClose }: TokenDocsDialogProps) {
    const baseUrl = `${window.location.origin}/api/tk`;

    const curlExample = `curl -H "Authorization: Bearer <ТОКЕН>" \\
  "${baseUrl}/measurements?fr=2026-01-01T00:00:00Z&to=2026-09-25T00:00:00Z&column=measure&order=DESC&offset=0&limit=100"`;

    const nodesExample = `curl -H "Authorization: Bearer <ТОКЕН>" \\
  "${baseUrl}/nodes"`;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>Доступ к данным с помощью токена</DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Базовый адрес: <b>{baseUrl}</b>
                </Alert>

                <Section title="Авторизация">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Во всех запросах токен передаётся в заголовке <b>Authorization</b>. Перед токеном указывается
                        префикс <b>Bearer</b>. Токен действует в пределах своего узла и всех вложенных в него узлов.
                    </Typography>
                    <CodeBlock code={'Authorization: Bearer <ТОКЕН>'} />
                </Section>

                <Divider sx={{ mb: 3 }} />

                <Section title="Эндпоинты">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Метод и путь</TableCell>
                                <TableCell>Описание</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {endpoints.map((row) => (
                                <TableRow key={row.name}>
                                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        {row.name}
                                    </TableCell>
                                    <TableCell>{row.description}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Section>

                <Divider sx={{ mb: 3 }} />

                <Section title="Параметры фильтрации">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Параметр</TableCell>
                                <TableCell>Тип</TableCell>
                                <TableCell>Описание</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.map((row) => (
                                <TableRow key={row.name}>
                                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        {row.name}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.type}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Section>

                <Divider sx={{ mb: 3 }} />

                <Section title="Сортировка и пагинация">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Если параметры <b>offset</b> или <b>limit</b> не переданы, сервер возвращает усреднённые данные
                        (до 500 точек). При указании хотя бы одного из них включается постраничная выдача сырых
                        измерений.
                    </Typography>
                    <CodeBlock code={`${baseUrl}/measurements?column=measure&order=DESC&offset=0&limit=100`} />
                </Section>

                <Divider sx={{ mb: 3 }} />

                <Section title="Пример ответа GET /measurements">
                    <CodeBlock code={responseExample} />
                </Section>

                <Divider sx={{ mb: 3 }} />

                <Section title="Примеры запросов">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Список узлов:
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <CodeBlock code={nodesExample} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Измерения за период:
                    </Typography>
                    <CodeBlock code={curlExample} />
                </Section>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" disableElevation>
                    Понятно
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default TokenDocsDialog;
